import React, { useState } from 'react';
import { Box, Container, Fade, IconButton, Tooltip } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { dashboardTheme } from '../../presentation/theme/dashboardTheme';
import DashboardHeader from './DashboardHeader';
import DashboardStats from './DashboardStats';
import EnhancedDataTable from './EnhancedDataTable';
import FilterDrawer from './FilterDrawer';
import SearchAutocomplete from './SearchAutocomplete';
import FileActionModal from './FileActionModal';
import NotificationSnackbar from '../common/NotificationSnackbar';
import { useMedicalDataList } from '../../hooks/useMedicalDataList';
import { useNotification } from '../../hooks/useNotification';
import { useNavigate } from 'react-router-dom';
import SkeletonLoader from '../common/SkeletonLoader';
import { MedicalDataFile, FilterOptions } from '../../types/medicalDataList';
import { FilterList } from '@mui/icons-material';

const MedicalDataDashboard: React.FC = () => {
  const navigate = useNavigate();
  const {
    files,
    filteredFiles,
    loading,
    stats,
    filterOptions,
    handleFileAction,
    updateFilterOptions,
    deleteFiles,
    updateFile,
  } = useMedicalDataList();
  const { notification, showSuccess, showError, showInfo, hideNotification } = useNotification();

  const [showUploadModal, setShowUploadModal] = useState(false);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<MedicalDataFile | null>(null);
  const [modalMode, setModalMode] = useState<'view' | 'edit'>('view');
  const [modalOpen, setModalOpen] = useState(false);

  const handleSearch = (query: string) => {
    updateFilterOptions({ searchQuery: query });
  };

  const handleUpload = () => {
    // CSVアップロード画面に遷移
    navigate('/appointments/upload');
  };

  const handleEdit = (file: MedicalDataFile) => {
    setSelectedFile(file);
    setModalMode('edit');
    setModalOpen(true);
  };

  const handleDelete = (file: MedicalDataFile) => {
    if (window.confirm(`「${file.fileName}」を削除してよろしいですか？`)) {
      handleFileAction({ action: 'delete', file });
      showSuccess(`「${file.fileName}」を削除しました`);
    }
  };

  const handleView = (file: MedicalDataFile) => {
    setSelectedFile(file);
    setModalMode('view');
    setModalOpen(true);
  };

  const handleDeleteMultiple = (ids: string[]) => {
    if (window.confirm(`選択した${ids.length}件のファイルを削除してよろしいですか？`)) {
      deleteFiles(ids);
      showSuccess(`${ids.length}件のファイルを削除しました`);
    }
  };

  const handleModalSave = (file: MedicalDataFile) => {
    updateFile(file.id, file);
    showSuccess(`「${file.fileName}」を更新しました`);
    setModalOpen(false);
  };

  const handleFilterChange = (newOptions: Partial<FilterOptions>) => {
    updateFilterOptions(newOptions);
  };

  const handleFilterReset = () => {
    updateFilterOptions({
      status: undefined,
      dateRange: undefined,
      tags: undefined,
      fileSizeRange: undefined,
    });
    showInfo('フィルターをリセットしました');
  };

  const openFilterDrawer = () => {
    setFilterDrawerOpen(true);
  };

  if (loading) {
    return (
      <ThemeProvider theme={dashboardTheme}>
        <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: 3 }}>
          <Container maxWidth="xl">
            <SkeletonLoader variant="stats" />
            <Box sx={{ mt: 4 }}>
              <SkeletonLoader variant="table" />
            </Box>
          </Container>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={dashboardTheme}>
      <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: 3 }}>
        <Container maxWidth="xl">
          <Fade in timeout={500}>
            <Box>
              {/* 検索とフィルター */}
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Box sx={{ flex: 1, maxWidth: 600 }}>
                    <SearchAutocomplete
                      onSearch={handleSearch}
                      files={filteredFiles}
                      searchHistory={[]} 
                    />
                  </Box>
                  <Tooltip title="フィルター">
                    <IconButton
                      onClick={openFilterDrawer}
                      sx={{
                        bgcolor: 'background.paper',
                        border: 1,
                        borderColor: 'divider',
                        '&:hover': {
                          bgcolor: 'action.hover',
                        },
                      }}
                    >
                      <FilterList />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              {/* 統計カード */}
              <Box sx={{ mb: 4 }}>
                <DashboardStats stats={stats} loading={loading} />
              </Box>

              {/* チャートエリア（Phase 3で実装） */}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 3, mb: 4 }}>
                {/* アップロードトレンドチャート */}
                <Box sx={{ height: 350, bgcolor: 'background.paper', borderRadius: 2, p: 3 }}>
                  {/* TODO: UploadTrendChart コンポーネント */}
                </Box>

                {/* ステータス分布チャート */}
                <Box sx={{ height: 350, bgcolor: 'background.paper', borderRadius: 2, p: 3 }}>
                  {/* TODO: StatusDistribution コンポーネント */}
                </Box>
              </Box>

              {/* データテーブル */}
              <EnhancedDataTable
                files={filteredFiles}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onView={handleView}
                onDeleteMultiple={handleDeleteMultiple}
                loading={loading}
              />

              {/* フィルタードロワー */}
              <FilterDrawer
                open={filterDrawerOpen}
                onClose={() => setFilterDrawerOpen(false)}
                filterOptions={filterOptions}
                onFilterChange={handleFilterChange}
                onReset={handleFilterReset}
              />

              {/* ファイルアクションモーダル */}
              <FileActionModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                file={selectedFile}
                mode={modalMode}
                onSave={handleModalSave}
              />

              {/* 通知スナックバー */}
              <NotificationSnackbar
                notification={notification}
                onClose={hideNotification}
              />
            </Box>
          </Fade>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default MedicalDataDashboard;